import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import NavBarNB from '../components/NavBarNB';
import FooterNB from '../components/FooterNB';
import CardNB from '../components/ui-nb/CardNB';
import ButtonNB from '../components/ui-nb/ButtonNB';
import IconNB from '../components/ui-nb/IconNB';
import StatusTimelineClient from '../components/client/StatusTimelineClient';
import PushSubscribe from '../components/client/PushSubscribe';
import { VAPID_PUBLIC_KEY } from '../lib/vapid';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useTranslation } from '../i18n';

interface StatusHistoryEntry {
  id: string;
  status_id: string;
  created_at: string;
  notes: string | null;
}

interface OrderPhoto {
  id: string;
  url: string;
  type: string;
  created_at: string;
}

interface BranchInfo {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  status_config: Record<string, { label: string; position: number }> | null;
}

interface OrderData {
  id: string;
  release_code: string;
  device: string;
  device_brand: string | null;
  device_model: string | null;
  failure_description: string | null;
  created_at: string;
  estimated_date: string | null;
  status: string;
  customer_name: string;
  technician_name: string | null;
  status_history: StatusHistoryEntry[];
  order_photos: OrderPhoto[];
  branch: BranchInfo | null;
}

export default function TrackRepairPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [trackingCode, setTrackingCode] = useState(searchParams.get('code') || '');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState<OrderData | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Auto-search when code is provided in URL (e.g. /track?code=JOG07LD7)
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl && codeFromUrl.trim() && isSupabaseConfigured()) {
      setTrackingCode(codeFromUrl);
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      setOrder(null);

      (async () => {
        try {
          const { data, error: queryError } = await supabase
            .rpc('get_public_order_by_code', { p_release_code: codeFromUrl.trim() })
            .single();
          if (queryError) {
            if (queryError.code === 'PGRST116') {
              setNotFound(true);
            } else {
              setError(t('tracking.connectionFailed') || 'Connection failed. Check your network and try again.');
            }
          } else if (data) {
            setOrder(data as OrderData);
          }
        } catch {
          setError(t('tracking.connectionFailed') || 'Connection failed. Check your network and try again.');
        }
        setIsLoading(false);
      })();
    }
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingCode.trim()) {
      setError(t('tracking.searchPlaceholder') || 'Please enter a tracking code');
      return;
    }

    if (!isSupabaseConfigured()) {
      setError('Database not configured');
      return;
    }

    setIsLoading(true);
    setError(null);
    setNotFound(false);
    setOrder(null);

    try {
      const { data, error: queryError } = await supabase
        .rpc('get_public_order_by_code', { p_release_code: trackingCode.trim() })
        .single();

      if (queryError) {
        if (queryError.code === 'PGRST116') {
          // Row not found
          setNotFound(true);
        } else {
          setError(t('tracking.connectionFailed') || 'Connection failed. Check your network and try again.');
        }
      } else if (data) {
        setOrder(data as OrderData);
      }
    } catch {
      setError(t('tracking.connectionFailed') || 'Connection failed. Check your network and try again.');
    }

    setIsLoading(false);
  };

  const handleViewDetails = () => {
    if (order) {
      navigate(`/en/client/equipment/${order.release_code}`);
    }
  };

  const progressPhotos = order?.order_photos
    ? order.order_photos.filter((p) => p.type === 'progress').slice(0, 20)
    : [];


  return (
    <div className="min-h-screen flex flex-col bg-nb-cream">
      <NavBarNB />

      <main className="flex-1 max-w-2xl mx-auto px-4 py-12 w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-black font-nb mb-2">
            {t('pages.track') || 'Track Your Repair'}
          </h1>
          <p className="text-nb-text-secondary">
            {t('placeholder.track.body') || 'Enter your tracking code to see the status of your repair'}
          </p>
        </div>

        <CardNB className="mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value)}
                placeholder={t('trackDemo.inputPlaceholder') || 'e.g. TELLOW-2024-ABC123'}
                className="flex-1 px-4 py-3 text-black placeholder-nb-text-muted bg-white border-2 border-black rounded-[2px] focus:outline-none focus:border-nb-yellow"
                disabled={isLoading}
              />
              <ButtonNB variant="primary" type="submit" loading={isLoading} className="w-full sm:w-auto">
                <IconNB name="search" size={20} />
                {isLoading ? (t('common.loading') || 'Searching...') : (t('trackDemo.cta') || 'Search')}
              </ButtonNB>
            </div>
          </form>
        </CardNB>

        {error && (
          <CardNB className="mb-8 border-2 border-nb-red">
            <div className="flex items-center justify-between">
              <p className="text-nb-red flex-1">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  handleSearch({ preventDefault: () => {} } as React.FormEvent);
                }}
                className="ml-4 text-nb-red underline text-sm"
              >
                {t('tracking.retryButton') || 'Retry'}
              </button>
            </div>
          </CardNB>
        )}

        {notFound && (
          <CardNB className="mb-8 border-2 border-nb-red">
            <p className="text-nb-red">{t('tracking.orderNotFound') || 'Order not found. Please verify and try again.'}</p>
          </CardNB>
        )}

        {order && (
          <>
            <CardNB variant="interactive" className="overflow-hidden mb-6">
              <div className="px-6 py-4 bg-nb-black">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm opacity-90">Tracking code</p>
                    <p className="text-white font-semibold">{order.release_code}</p>
                  </div>
                  <span className="px-3 py-1 text-sm font-medium bg-white text-nb-black border-2 border-black rounded-[2px]">
                    {order.status}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 flex items-center justify-center text-2xl bg-nb-cream border-2 border-black rounded-[2px]">
                    📱
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-black">
                      {order.device_brand || ''} {order.device_model || order.device || ''}
                    </h2>
                    <p className="text-nb-text-muted text-sm">
                      {order.device || 'Unknown device'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
                  <div>
                    <p className="text-nb-text-muted">{t('clientEquipment.brandModel') || 'Client'}</p>
                    <p className="font-medium text-black">{order.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-nb-text-muted">{t('clientEquipment.receptionDate') || 'Reception date'}</p>
                    <p className="text-black">{order.created_at}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-nb-text-muted">{t('clientEquipment.problemDescription') || 'Problem description'}</p>
                    <p className="text-black">{order.failure_description || '-'}</p>
                  </div>
                </div>

                <ButtonNB variant="primary" onClick={handleViewDetails} className="w-full">
                  View full details
                  <IconNB name="chevronRight" size={20} />
                </ButtonNB>
              </div>
            </CardNB>

            {/* Status Timeline */}
            <CardNB className="mb-6">
              <h3 className="font-semibold text-black mb-4">
                {t('clientEquipment.yourStatus') || 'Your Status'}
              </h3>
              <StatusTimelineClient
                history={order.status_history || []}
                statusConfig={order.branch?.status_config || null}
                isLoading={false}
              />
            </CardNB>

            {/* Progress Photos */}
            {progressPhotos.length > 0 && (
              <CardNB className="mb-6">
                <h3 className="font-semibold text-black mb-4">
                  {t('clientEquipment.progressPhotos') || 'Progress Photos'}
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {progressPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="aspect-square bg-nb-cream border-2 border-black rounded-[2px] flex items-center justify-center overflow-hidden"
                    >
                      <img
                        src={photo.url}
                        alt="Progress photo"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </CardNB>
            )}

            {progressPhotos.length === 0 && (
              <CardNB className="mb-6">
                <div className="text-center py-6">
                  <p className="text-nb-text-muted">{t('tracking.noPhotos') || 'No progress photos available'}</p>
                </div>
              </CardNB>
            )}

            {/* Branch Contact */}
            {order.branch && (order.branch.phone || order.branch.address) && (
              <CardNB className="mb-6">
                <h3 className="font-semibold text-black mb-3">Contact</h3>
                <div className="space-y-2 text-sm">
                  {order.branch.phone && (
                    <div className="flex items-center gap-2">
                      <IconNB name="phone" size={16} />
                      <a href={`tel:${order.branch.phone}`} className="text-nb-black font-bold hover:underline">
                        {order.branch.phone}
                      </a>
                    </div>
                  )}
                  {order.branch.address && (
                    <div className="flex items-center gap-2">
                      <IconNB name="location" size={16} />
                      <span className="text-black">{order.branch.address}</span>
                    </div>
                  )}
                </div>
              </CardNB>
            )}

            {/* Push Notification Subscribe */}
            <PushSubscribe
              orderId={order.id}
              releaseCode={order.release_code}
              vapidPublicKey={VAPID_PUBLIC_KEY}
            />

            {/* Track Another */}
            <div className="text-center">
              <Link to="/track" className="text-nb-black font-bold hover:underline text-sm">
                {t('tracking.trackAnother') || 'Track another repair'}
              </Link>
            </div>
          </>
        )}

        <div className="mt-12 text-center text-sm text-nb-text-muted">
          <p>Don't have a code?</p>
          <p className="mt-1">Contact your repair shop to get it.</p>
        </div>
      </main>

      <FooterNB />
    </div>
  );
}